import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";

/**
 * The content formatting styles for a popup. One of 'flat' (one message per line), 'list' (bulleted list), or 'list-after-first' (first message is shown on its own line, followed by a bulleted list for any remaining messages).
 */
type PopupContentStyle = "flat" | "list" | "list-after-first";

/**
 * The types which can be formatted as popup content.
 */
type PopupContent = string | ErrorMessage | (string | ErrorMessage)[];

/**
 * The model for a generic popup dialogue.
 */
class PopupModel {
    /**
     * The message or messages to display.
     */
    public readonly content: PopupContent;

    /**
     * How the messages should be formatted in the popup.
     */
    public readonly contentStyle: PopupContentStyle = "flat";

    /**
     * Should bold the first line of the content.
     */
    public readonly boldFirstLineOfList: boolean = false;

    /**
     * Should we add additional space between the first line and second line in the content list.
     */
    public readonly addAdditionalSpace: boolean = false;

    /**
     * The popup title to display.
     */
    public readonly title: string = cacheStore.GetString("ErrorLabel");

    /**
     * Whether to show the main OK button.
     */
    public readonly showOk: boolean = true;

    /**
     * Whether to show a cancel button.
     */
    public readonly showCancel: boolean = false;

    /**
     * The display text for the Cancel button.
     */
    public readonly cancelLabel: string;

    /**
     * The display text for the OK button.
     */
    public readonly okLabel: string = cacheStore.GetString("OKLabel");

    /**
     * A callback to invoke when the user clicks the OK button.
     */
    public readonly okAction: (() => void) = null;

    /**
     * Variant name to apply to the OK button, replacing the default variant name.
     */
    public readonly okVariant: "brand" | "neutral" | "error" | null = null;

    /**
     * The display text for a custom button to display.
     */
    public readonly altButtonLabel: string = null;

    /**
     * A callback to invoke when the user clicks the custom button.
     */
    public readonly altButtonAction: (() => void) = null;

    /**
     * Variant name to apply to the custom button, replacing the default variant name.
     */
    public readonly altButtonVariant: "brand" | "neutral" | "error" | null = null;

    /**
     * A callback to invoke when the user clicks the cancel button or the x button in the header. Will not be triggered if any other button is clicked.
     */
    public readonly cancelAction: (() => void) = null;

    /**
     * CSS classes to add to the main popup element.
     */
    public readonly popupClass: string = null;

    /**
     * Whether the popup should resize automatically to fit wider content.
     */
    public readonly autoWidth: boolean = false;

    /**
     * Whether to show the top-right X button to close the popup. This is equivalent to the cancel button.
     */
    public readonly showCloseX: boolean = false;

    /**
     * Whether to horizontally center the popup on the screen.
     */
    public readonly centerPopupHorizontally: boolean = false;

    /**
     * Construct an instance.
     * @param options The popup options.
     */
    constructor(options?: Partial<PopupModel>) {
        options = options || {};

        this.content = this.formatContent(options.content);
        this.contentStyle = options.contentStyle || "flat";
        this.title = options.title || cacheStore.GetString("ErrorLabel");
        this.showCancel = !!options.showCancel;
        this.showOk = typeof options.showOk != "undefined" ? options.showOk : true;
        this.okLabel = options.okLabel || cacheStore.GetString("OKLabel");
        this.cancelLabel = options.cancelLabel || cacheStore.GetString("CancelLabel");
        this.okAction = options.okAction || null;
        this.okVariant = options.okVariant || null;
        this.altButtonLabel = options.altButtonLabel || null;
        this.altButtonAction = options.altButtonAction || null;
        this.altButtonVariant = options.altButtonVariant || null;
        this.cancelAction = options.cancelAction || null;
        this.popupClass = options.popupClass || null;
        this.autoWidth = !!options.autoWidth;
        this.centerPopupHorizontally = options.centerPopupHorizontally || false;
        this.showCloseX = options.showCloseX || false;
        this.addAdditionalSpace = options.addAdditionalSpace || false;
        this.boldFirstLineOfList = options.boldFirstLineOfList || false;
    }

    /**
     * Get the formatted content to display.
     */
    public getContentForDisplay(): string[] {
        return this.content as string[]; // content is formatted in the constructor
    }

    /**
     * Format input content for display.
     * @param messages The content to format.
     */
    private formatContent(content: PopupContent): string[] {
        return sessionStore.formatErrorsForDisplay(content);
    }
}

export { PopupModel };
